"use client";

import { createContext, useContext } from "react";

/**
 * Pages whose URL is itself the credential (the proposal, order tracker, share
 * and VirtualSpace tier links, and the tech and dispatch links) ship
 * `Referrer-Policy: strict-origin` from next.config.js. An element that sets
 * its own `referrerPolicy` is not bound by that header, and the Map View,
 * Street View and Matterport embeds set the full-URL
 * `no-referrer-when-downgrade`, which sent the token to Google and Matterport.
 *
 * Each token URL segment's layout wraps its pages in this provider, and those
 * embeds read it: on a token URL page they send only the origin, everywhere
 * else they keep their own policy. A server layout can render it; it only
 * provides context.
 *
 * A referrer policy only governs what a request sends. The viewer tabs' Street
 * View pane runs Google's Maps JavaScript API, and their 3D pane runs CesiumJS
 * against Google's tiles, inside the page itself, where script can read
 * `location.href` whatever the policy.
 */
const OnTokenUrlPage = createContext(false);

export function TokenUrlReferrerPolicy({ children }: { children: React.ReactNode }) {
  return <OnTokenUrlPage.Provider value>{children}</OnTokenUrlPage.Provider>;
}

/** `strict-origin` on a token URL page, otherwise the embed's own policy. */
export function useEmbedReferrerPolicy<Policy extends string>(
  policy: Policy,
): Policy | "strict-origin" {
  return useContext(OnTokenUrlPage) ? "strict-origin" : policy;
}
